<template>
	<FieldSet class="ticketAssignment" :label="$t('text.ticketAssignment')" :infoText="$t('text.ticketAssignmentInfo')">
		<div class="assignmentSwitch">
			<div>
				<h2 style="color:#000;line-height:1.1">{{ $t('text.allowChannelSpecificAssignments') }}</h2>
				<p v-html="$t('text.allowChannelSpecificAssignmentsHelpText')"/>
			</div>
			<SwitchField
				v-model="hasTicketAssignments"
				:hideSwitchClear="true"
			/>
		</div>
		<div v-if="hasTicketAssignments" class="ticketAssignmentSelects">
			<div class="header">
					<span class="productColumnHeader">{{ $t('text.product') }}</span>
					<span class="channelsColumnHeader">{{ $t('text.salesChannels') }}</span>
			</div>
			<div class="ticketAssignmentSelects-container">
				<div class="ticketAssignmentSelects-container-item" v-for="(ticketAssignment, i) in ticketAssignmentsModel" :key="ticketAssignment.ticketOption?.ticketOptionId ?? i">
					<div class="ticketLabel">
						{{ getTicketAssignmentText(ticketAssignment) }}
					</div>
					<div class="ticketSelector">
						<SalesChannelSelector
							:modelValue="ticketAssignment.salesChannelsModel"
							:clients="clients"
							:propClientAssignments="propClientAssignments"
							:serviceProvider="serviceProvider"
							:dataCy="dataCy"
							:app="app"
							:clientAssignmentLocked="clientAssignmentLocked"
							:salesChannelSelectionLocked="salesChannelSelectionLocked"
							:isWaiting="isWaiting"
							:locked="locked"
							:compact-chips="false"
							:collapse-chips-when-closed="true"
							:collapsed-chip-limit="10"
							@update:modelValue="updateTicketAssignmentModel($event, i)"
						/>
					</div>
				</div>
			</div>
		</div>
	</FieldSet>
</template>

<script>
import Common from '@/mixins/Common.vue'
import FieldSet from '../../views/applications/packageDesigner/FieldSet.vue'
import SalesChannelSelector from './SalesChannelSelector.vue'
import TicketAssignmentExecutive from '../../../../api2/src/modules/ticket-assignment/TicketAssignmentExecutive'
import SwitchField from '../../components/fields/SwitchField.vue'
import { buildSalesChannelsArrayFromModel, buildSalesChannelsModel, mergeSalesChannelsModels } from '@/utils/sales-channel-assignment-adapter'
import isEqual from 'lodash/isEqual'

export default {
	name: 'TicketAssignmentsSection',
	components: { FieldSet, SalesChannelSelector, SwitchField },
	mixins: [ Common ],
	props: {
		productId: String,
		ticketOptions: Array,
		clients: Array,
		propClientAssignments: Array,
		serviceProvider: Object,
		dataCy: String,
		app: String,
		clientAssignmentLocked: Boolean,
		salesChannelSelectionLocked: Boolean,
		isWaiting: Boolean,
		locked: Boolean,
		modelValue: {
			type: Boolean,
			default: false
		},
		productSalesChannelsModel: {
			type: Array,
			default: () => []
		},
	},
	data: () => ({
		hasTicketAssignments: false,
		initHasTicketAssignments: false,
		ticketAssignments: [],
		ticketAssignmentsModel: [],
		initialModelSet: false,
		modelCopy: [],
		initTicketAssignmentsModel: [],
		assignmentLookupCache: {}
	}),
	computed: {
		currency() {
			return this.$store.state.selectedClient.fields.currency.de
		},
		selectedClient() {
			return this.$store.state.selectedClient
		},
		assignmentClients() {
			const byId = new Map()
			for (const client of this.clients ?? []) {
				if (client?.sys?.id) {
					byId.set(client.sys.id, client)
				}
			}
			for (const ca of this.propClientAssignments ?? []) {
				const client = ca?.fields?.client?.de
				if (client?.sys?.id && !byId.has(client.sys.id)) {
					byId.set(client.sys.id, client)
				}
			}
			return Array.from(byId.values())
		},
	},
	watch: {
		modelValue: {
			immediate: true,
			handler(value) {
				this.hasTicketAssignments = value === true
			}
		},
		ticketOptions(newVal, oldVal) {
			const options = Array.isArray(newVal) ? newVal : []
			if (!oldVal && !this.initialModelSet && options.length && this.ticketAssignments?.length) {
				this.setInitialModel()
				this.initialModelSet = true
				return
			}
			this.syncRowsWithTicketOptions()
		},
		hasTicketAssignments(val, oldVal) {
			this.$emit('update:modelValue', val === true)
			if (!val) {
				this.modelCopy = JSON.parse(JSON.stringify(this.ticketAssignmentsModel))
				this.ticketAssignmentsModel = []
			} else {
				this.assignmentLookupCache = {}
				const switchedFromProductLevel = oldVal === false
				const fallbackRows = switchedFromProductLevel
					? this.buildRowsFromProductSalesChannels()
					: this.buildRowsFromTicketOptions()
				this.ticketAssignmentsModel = fallbackRows
				this.emitSupersetModel()
			}
			this.$emit('changed')
		},
		clients() {
			for (const row of this.ticketAssignmentsModel ?? []) {
				row.salesChannelsModel = buildSalesChannelsModel(this.assignmentClients, row.salesChannels)
				this.ensureMandatorySalesChannelsForModel(row.salesChannelsModel)
			}
		},
	},
	created() {
		this.getTicketAssignments()
	},
	methods: {
		sendData() {
			const sourceRows = this.hasTicketAssignments ? this.ticketAssignmentsModel : this.modelCopy
			const ticketOptionAssignments = this.getNormalizedTicketAssignments(sourceRows)
			return {
				ticketOptionAssignments,
				ticketLevelChannelingEnabled: this.hasTicketAssignments === true,
				modeChanged: this.hasTicketAssignments !== this.initHasTicketAssignments,
				changed: !isEqual(ticketOptionAssignments, this.initTicketAssignmentsModel)
			}
		},
		getNormalizedTicketAssignments(rows = this.ticketAssignmentsModel) {
			const normalized = []
			for (const row of rows ?? []) {
				const option = row?.ticketOption ?? {}
				const ticketTypeId = option.ticketTypeId ?? null
				const reductionId = option.reductionId ?? null
				if (!ticketTypeId || !reductionId) {
					continue
				}
				const salesChannels = (row?.salesChannels ?? [])
					.filter(sc => sc?.clientId)
					.map(sc => ({
						clientId: sc.clientId,
						salesChannelIds: (sc.salesChannelIds ?? []).slice().sort(),
						resellerCategoryIds: (sc.resellerCategoryIds ?? []).slice().sort()
					}))
					.sort((a, b) => a.clientId.localeCompare(b.clientId))
				normalized.push({
					ticketOption: {
						ticketOptionId: option.ticketOptionId ?? option.id ?? option.sys?.id ?? null,
						ticketTypeId,
						reductionId
					},
					salesChannels
				})
			}
			return normalized.sort((a, b) => {
				const aKey = `${a.ticketOption.ticketTypeId}::${a.ticketOption.reductionId}`
				const bKey = `${b.ticketOption.ticketTypeId}::${b.ticketOption.reductionId}`
				return aKey.localeCompare(bKey)
			})
		},
		setInitialTicketAssignmentsModel() {
			this.initTicketAssignmentsModel = this.getNormalizedTicketAssignments(this.ticketAssignmentsModel)
		},
		async reloadTicketOptionAssignments() {
			await this.getTicketAssignments()
		},

		validateMandatoryTicketAssignments() {
			if (!this.hasTicketAssignments) {
				return true
			}
			if (!Array.isArray(this.ticketOptions) || !this.ticketOptions.length) {
				return false
			}
			for (const row of this.ticketAssignmentsModel ?? []) {
				const salesChannels = row?.salesChannels ?? []
				const hasValidSelection = salesChannels.some(sc => Array.isArray(sc.salesChannelIds) && sc.salesChannelIds.length > 0)
				if (!hasValidSelection) {
					return false
				}
			}
			return true
		},
		validateTicketAssignments() {
			if (!this.validateMandatoryTicketAssignments()) {
				return { valid: false, key: 'text.missingFieldsError' }
			}
			return { valid: true, key: null }
		},
		updateTicketAssignmentModel(value, index) {
			const salesChannelsModel = Array.isArray(value) ? value : []
			this.ensureMandatorySalesChannelsForModel(salesChannelsModel)
			const salesChannels = buildSalesChannelsArrayFromModel(salesChannelsModel, this.clients)
			this.ticketAssignmentsModel = (this.ticketAssignmentsModel ?? []).map((row, rowIndex) => {
				if (rowIndex !== index) {
					return row
				}
				return {
					...row,
					salesChannelsModel,
					salesChannels
				}
			})
			this.emitSupersetModel()
			this.$emit('changed')
		},
		getTicketAssignmentText(ta) {
			if (!ta?.ticketOption?.ticketTypeTitle || !ta?.ticketOption?.reductionTitle) {
				return ''
			}
			const price = ta.ticketOption.price
			const formatted = Number.isFinite(Number(price)) ? ` | ${this.currency} ${Number(price).toFixed(2)}` : ''
			return `${ta.ticketOption.ticketTypeTitle} | ${ta.ticketOption.reductionTitle}${formatted}`
		},
		async getTicketAssignments() {
			const ticketAssignmentExecutive = new TicketAssignmentExecutive(this)
			const serviceProviderId = this.$store.state.selectedServiceProvider.sys.id
			this.ticketAssignments = await ticketAssignmentExecutive.getProductAssignmentsForAllClients(this.productId, this.selectedClient.sys.id, serviceProviderId)
			this.initHasTicketAssignments = this.modelValue === true || this.ticketAssignments.length > 0
			this.hasTicketAssignments = this.modelValue === true || this.ticketAssignments.length > 0
			if (this.ticketOptions) {
				this.setInitialModel()
				this.initialModelSet = true
			}
		},
		setInitialModel() {
			this.ticketAssignmentsModel = this.buildRowsFromTicketOptions()
			this.syncRowsWithTicketOptions()
			this.modelCopy = JSON.parse(JSON.stringify(this.ticketAssignmentsModel))
			this.setInitialTicketAssignmentsModel()
			if (this.hasTicketAssignments) {
				this.emitSupersetModel()
			}
		},
		buildRowsFromTicketOptions() {
			const groupedAssignments = new Map()
			for (const assignment of this.ticketAssignments ?? []) {
				const key = assignment.ticketOptionId
				if (!groupedAssignments.has(key)) {
					groupedAssignments.set(key, [])
				}
				groupedAssignments.get(key).push({
					salesChannelIds: assignment.salesChannelIds ?? [],
					resellerCategoryIds: assignment.resellerCategoryIds ?? [],
					clientId: assignment.clientId
				})
			}
			const rows = []
			for (const option of this.ticketOptions ?? []) {
				const salesChannels = groupedAssignments.get(option.ticketOptionId) ?? []
				const row = {
					ticketOption: option,
					salesChannels,
					salesChannelsModel: buildSalesChannelsModel(this.assignmentClients, salesChannels)
				}
				this.ensureMandatorySalesChannelsForModel(row.salesChannelsModel)
				rows.push(row)
			}
			return rows
		},
		buildRowsFromProductSalesChannels() {
			const rows = []
			for (const option of this.ticketOptions ?? []) {
				const salesChannelsModel = JSON.parse(JSON.stringify(this.productSalesChannelsModel ?? []))
				this.ensureMandatorySalesChannelsForModel(salesChannelsModel)
				const salesChannels = buildSalesChannelsArrayFromModel(salesChannelsModel, this.clients)
				rows.push({
					ticketOption: option,
					salesChannels,
					salesChannelsModel
				})
			}
			return rows
		},
		syncRowsWithTicketOptions() {
			const optionsById = new Map((this.ticketOptions ?? []).map(option => [option.ticketOptionId, option]))
			const currentRows = new Map((this.ticketAssignmentsModel ?? []).map(row => [row.ticketOption?.ticketOptionId, row]))
			const rebuiltRows = []
			for (const option of this.ticketOptions ?? []) {
				const existingRow = currentRows.get(option.ticketOptionId)
				if (existingRow) {
					rebuiltRows.push({
						...existingRow,
						ticketOption: optionsById.get(option.ticketOptionId) ?? option
					})
					continue
				}
				rebuiltRows.push({
					ticketOption: option,
					salesChannels: [],
					salesChannelsModel: []
				})
			}
			this.ticketAssignmentsModel = rebuiltRows
			if (!this.hasTicketAssignments) {
				this.modelCopy = JSON.parse(JSON.stringify(this.ticketAssignmentsModel))
			}
		},
		emitSupersetModel() {
			if (!this.hasTicketAssignments) {
				return
			}
			const additions = this.ticketAssignmentsModel.map(row => row.salesChannelsModel ?? [])
			const flattenedAdditions = additions.flatMap(row => row ?? [])
			const mergedModel = mergeSalesChannelsModels([], flattenedAdditions, this.assignmentLookupCache)
			this.$emit('merge-into-product-model', mergedModel)
		},
		ensureMandatorySalesChannelsForModel(model) {
			if (!Array.isArray(model) || !Array.isArray(this.assignmentClients)) {
				return
			}
			for (const clientModel of model) {
				const client = this.assignmentClients.find(c => c.sys.id === clientModel.id)
				if (!client?.fields?.salesChannels?.de) {
					continue
				}
				const selected = Array.isArray(clientModel.selected) ? clientModel.selected : []
				const selectedIds = new Set(selected.map(s => s.id))
				for (const sc of client.fields.salesChannels.de) {
					if (sc.fields?.mandatory?.de && !selectedIds.has(sc.sys.id)) {
						selected.push({
							id: sc.sys.id,
							title: sc.fields.title,
							isSelected: true,
							selected: []
						})
					}
				}
				clientModel.selected = selected
			}
		},
	},
}
</script>

<style lang="scss" scoped>
	.ticketAssignment {
		margin-top: 32px;
		.ticketAssignmentSelects {
			.header {
				display: flex;
				border-bottom: 1px solid #e5e5e5;
				height: 60px;
				> span {
					padding: 14px 12px;
					font-size: 15px;
					font-weight: 500;
					line-height: 20px;
					letter-spacing: -0.037px;
					display: flex;
					margin-top: auto;
					margin-bottom: auto;
				}
				> .productColumnHeader {
					flex: 0 0 36%;
				}
				> .channelsColumnHeader {
					flex: 1 1 64%;
				}
			}
			&-container {
				&-item {
					display: flex;
					align-items: stretch;
					border-bottom: 1px solid #e5e5e5;
					> .ticketLabel {
						flex: 0 0 36%;
						min-height: 60px;
						display: flex;
						align-items: center;
						padding: 0px 12px;
					}
					> .ticketSelector {
						flex: 1 1 64%;
						display: flex;
						align-items: center;
						padding: 8px 12px;
						min-height: 60px;
					}
				}
			}
		}
		.assignmentSwitch {
			display: flex;
			margin: 16px 0px;
			justify-content: space-between;
			h2 {
				font-size: 15px;
				font-style: normal;
				font-weight: 400;
				line-height: 20px;
				letter-spacing: -0.037px;
			}
			p {
				color:#8F8F8F;
				font-size: 12px;
				font-weight: 600;
				line-height: 16px;
				letter-spacing: 0.001px;
			}

			:deep(.SwitchField .v-switch .v-selection-control) {
				min-height: auto;
			}
			:deep(.SwitchField .v-switch .v-selection-control__wrapper) {
				height: 32px;
			}
			:deep(.SwitchField .v-switch .v-selection-control__input) {
				width: 32px;
				height: 32px;
			}
		}
	}
</style>
